import React from 'react';

import { useTranslation } from 'react-i18next';
import { useSupplierSurvey } from '../../hooks';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close, Visibility } from '@mui/icons-material';
import { StyledPanel } from 'src/components/StyledPanel';
import { Link } from 'react-router-dom';
import { usePlatformNavigation } from 'src/utils/navigation';

interface Props {
  id?: number;
  open: boolean;
  onClose: () => void;
  organizationId: number;
}

function responseStatus(status: boolean | null | undefined) {
  if (status === true) {
    return 'yes';
  } else if (status === false) {
    return 'no';
  } else {
    return 'noResponse';
  }
}

export default function PreviewDialog({
  id,
  open,
  onClose,
  organizationId,
}: Props) {
  const { navigateToOrganization } = usePlatformNavigation();

  const { t } = useTranslation(undefined, {
    keyPrefix: 'ghg.scope3GoodsAndServices.productsFromSuppliers.preview',
  });
  const { t: translate } = useTranslation();

  const { data } = useSupplierSurvey(organizationId);

  const survey = data?.find((survey) => survey.id === id);

  const answer = survey?.answer;

  const openDetailedEsgPreview = () =>
    window.open(
      `/questionnaire-readonly/${answer?.questionnaire_answer_esg}`,
      '_blank'
    );

  const openDetailedEnvironmentPreview = () =>
    window.open(
      `/questionnaire-readonly/${answer?.questionnaire_answer_environment}`,
      '_blank'
    );

  const goToSupplierOrganization = () => {
    if (survey?.organization_id) {
      navigateToOrganization(survey.organization_id);
    }
  };

  const hasSedexQuestionnaire = responseStatus(answer?.has_sedex_questionnaire);

  const wantsToUsePlatform = responseStatus(answer?.wants_to_use_platform);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <IconButton
        sx={{ position: 'absolute', right: '10px', top: '10px' }}
        onClick={onClose}
      >
        <Close />
      </IconButton>
      <DialogTitle>
        <Typography>{t('title')}</Typography>
      </DialogTitle>

      <DialogContent>
        <StyledPanel sx={{ mb: '24px' }}>
          <Typography sx={{ mb: '24px' }}>
            {t('companyName')}: <b>{answer?.company_name}</b>
          </Typography>
          <Typography sx={{ mb: '24px' }}>
            {t('contactEmail')}: <b>{answer?.contact_email}</b>
          </Typography>
          <Typography>
            {t('vatIdentifier')}: <b>{answer?.vat_identifier}</b>
          </Typography>
        </StyledPanel>

        <StyledPanel sx={{ mb: '24px' }}>
          <Typography sx={{ mb: '24px' }}>
            {t('hasSedexQuestionnaire')}:{' '}
            <b>{translate(hasSedexQuestionnaire)}</b>
          </Typography>
          {answer?.has_sedex_questionnaire && (
            <>
              <Typography sx={{ mb: '24px' }}>
                {t('zcCode')}: <b>{answer.zc_code}</b>
              </Typography>
              <Link to={answer.saq_file?.url as string} target="_blank">
                <Typography>{t('downloadSaqFile')}</Typography>
              </Link>
            </>
          )}

          {answer?.has_sedex_questionnaire === false && (
            <Button startIcon={<Visibility />} onClick={openDetailedEsgPreview}>
              {t('detailedPreviewEsg')}
            </Button>
          )}
        </StyledPanel>

        <StyledPanel>
          <Typography>
            {t('wantsToUsePlatform')}: <b>{translate(wantsToUsePlatform)}</b>
          </Typography>
          {answer?.wants_to_use_platform && (
            <Button
              startIcon={<Visibility />}
              onClick={goToSupplierOrganization}
              sx={{ mt: '24px' }}
              disabled={survey?.organization_id === null}
            >
              {t('goToOrganization')}
            </Button>
          )}
          {answer?.wants_to_use_platform === false && (
            <Button
              startIcon={<Visibility />}
              onClick={openDetailedEnvironmentPreview}
              sx={{ mt: '24px' }}
            >
              {t('detailedPreviewEnvironment')}
            </Button>
          )}
        </StyledPanel>
      </DialogContent>
    </Dialog>
  );
}
